import { formatUkAndUtc } from './format-date';

/** An instant for copy: UK local time with the UTC time. */
export function atCopy(iso: string): string {
    const when = formatUkAndUtc(iso);
    return `${when.uk} (${when.utc})`;
}

export function axisLabel(axis: string): string {
    switch (axis) {
        case 'PRICE':
            return 'price';
        case 'AREA':
            return 'area';
        case 'TYPE':
            return 'property type';
        case 'BEDS':
            return 'bedrooms';
        default:
            return axis;
    }
}

/** Whether the feed plans wildcards for this Search, and if not, which of its gates said no. */
export function planCopy(planState: string, reason: string | null): string {
    if (planState === 'PLANNED') return 'The feed plans wildcards for this Search.';
    switch (reason) {
        case 'RENTING':
            return 'The feed plans no wildcards: this is a rent Search.';
        case 'NOT_COVERED':
            return 'The feed plans no wildcards: the index does not serve this Search.';
        case 'UNSCORED':
            return 'The feed plans no wildcards: the household has no scores.';
        default:
            return reason ? `The feed plans no wildcards (${reason}).` : 'The feed plans no wildcards.';
    }
}

export function rateStateCopy(state: string): string {
    switch (state) {
        case 'BASE':
            return 'at the base rate';
        case 'RAISED':
            return 'raised above the base rate';
        case 'BACKED_OFF':
            return 'backed off below the base rate';
        case 'NONE':
            return 'no wildcards (rate is zero)';
        default:
            return state;
    }
}

function percent(rate: number): string {
    return `${Math.round(rate * 1000) / 10}%`;
}

export function rateCopy(rate: number, oneIn: number | null, baseRate: number, state: string): string {
    const pace = oneIn === null ? 'no wildcards' : `about 1 in ${oneIn}`;
    return `Rate ${percent(rate)} (${pace}), ${rateStateCopy(state)}; base rate ${percent(baseRate)}.`;
}

export function timesShownCopy(timesShown: number, lookbackDays: number): string {
    return `Shown ${timesShown} ${timesShown === 1 ? 'time' : 'times'} in the last ${lookbackDays} days.`;
}

export function axisOffSourceCopy(source: string): string {
    switch (source) {
        case 'SHOW_FEWER':
            return 'the member asked to see fewer';
        case 'FIRM_ANSWER':
            return 'from a firm answer';
        default:
            return source;
    }
}

export function priceFirmCopy(price: { isFirm: boolean; answerKey: string; answer: string; answeredUtc: string | null }): string {
    // The backend always sends the firmness:price key and a "firm" answer; only a real answer has a time.
    const from =
        price.answerKey && (price.isFirm || price.answeredUtc)
        ? ` (${price.answerKey}: ${price.answer}${price.answeredUtc ? `, answered ${atCopy(price.answeredUtc)}` : ''})`
        : '';
    return price.isFirm ? `Price counts as firm${from}.` : `Price does not count as firm${from}.`;
}

export function pendingCopy(pending: string): string {
    switch (pending) {
        case 'CARD_OWED':
            return 'A Results changed card is waiting for the member\'s next read.';
        case 'NO_CARD':
            return 'No Results changed card is pending.';
        case 'NOT_RANKED':
            return 'None pending: the feed cannot rank this Search (not covered, no scores, or scoring withheld), so it owes no card.';
        case 'NOT_EVALUATED':
            return 'Not evaluated (no scored feed read).';
        default:
            return pending;
    }
}

/** The STORED baseline state, with the dates it carries. */
export function storedStateCopy(
    state: string,
    notedSince: string | null,
    lapses: string | null,
): string {
    switch (state) {
        case 'NO_BASELINE':
            return 'No baseline is stored yet.';
        case 'NO_CARD_NOTED':
            return 'A baseline is stored; no card is noted as owed.';
        case 'CARD_NOTED':
            return `A card was noted as owed${notedSince ? ` since ${atCopy(notedSince)}` : ''}${lapses ? `; it lapses ${atCopy(lapses)}` : ''}.`;
        case 'CARD_LAPSED':
            return `A noted card has lapsed${lapses ? ` (${atCopy(lapses)})` : ''}.`;
        default:
            return state;
    }
}

export function baselineMovedCopy(movedUtc: string | null): string {
    return movedUtc
        ? `Baseline last moved ${atCopy(movedUtc)}. "Got it" and an ordinary follow both move it, so this is not a record of the member acknowledging a card.`
        : 'No baseline has been stored, so there is nothing to have moved.';
}

/** What the member's next read would do to the baseline. Reported, never done by an inspection. */
export function readWouldWriteCopy(write: string | null, pending: string | null = null): string {
    if (write === null && pending === 'NOT_EVALUATED') {
        return "Unknown: nothing was evaluated, so it is not known what the member's next read would write.";
    }
    switch (write) {
        case 'CAPTURE':
            return "The member's next read would capture a baseline.";
        case 'FOLLOW':
            return "The member's next read would follow the current ranking (re-take the baseline).";
        case 'NOTE_CARD_SHOWN':
            return "The member's next read would note the card as owed (starting its 7-day clock).";
        case 'NONE':
        case null:
            return "The member's next read would write nothing to the baseline.";
        default:
            return write;
    }
}

export function fitBandLabel(band: string): string {
    switch (band) {
        case 'STRONG':
            return 'Strong fit';
        case 'GOOD':
            return 'Good fit';
        case 'MIXED':
            return 'Mixed fit';
        case 'POOR':
            return 'Poor fit';
        case 'EARLY':
            return 'Early read';
        case 'NOT_SCORED':
            return 'Not scored';
        case 'CAPPED':
            return 'Capped';
        default:
            return band;
    }
}

export function moverCopy(m: {
    address: string;
    direction: string;
    rank: number;
    previousRank: number;
    places: number;
    score: number;
    reason: string;
    fitBand: string;
}): string {
    const dir = m.direction === 'UP' ? 'up' : m.direction === 'DOWN' ? 'down' : m.direction;
    return `${m.address}: ${dir} ${m.places} ${m.places === 1 ? 'place' : 'places'} (score rank #${m.previousRank} to #${m.rank}) · ${fitBandLabel(m.fitBand)}, score ${m.score} · ${m.reason}`;
}

export function engagementKindLabel(kind: string): string {
    switch (kind) {
        case 'SAVED':
            return 'Saved';
        case 'UNSAVED':
            return 'Unsaved';
        case 'DISMISSED':
            return 'Dismissed';
        case 'OPENED':
            return 'Opened';
        case 'DWELL':
            return 'Dwell';
        case 'PHOTO_SCROLL':
            return 'Photos scrolled';
        case 'FLOORPLAN_OPENED':
            return 'Floorplan opened';
        case 'RETURNED':
            return 'Returned to';
        case 'WILDCARD_SHOWN':
            return 'Wildcard shown';
        default:
            return kind;
    }
}

/** The quantity, in its own unit: dwell is milliseconds, photo scroll counts photos; other kinds carry none. */
function quantityCopy(kind: string, quantity: number | null): string {
    if (quantity === null) return '';
    if (kind === 'DWELL') return ` ${(quantity / 1000).toFixed(1)} s`;
    if (kind === 'PHOTO_SCROLL') return ` ${quantity} ${quantity === 1 ? 'photo' : 'photos'}`;
    return '';
}

export const PREFERENCES_UNKNOWN_COPY =
    "Can't tell whether preferences changed (no baseline, or the Search can't be read).";

export function engagementEventCopy(e: {
    kind: string;
    occurredUtc: string;
    listingId: string;
    quantity: number | null;
    wildcardAxis: string | null;
}): string {
    const axis = e.wildcardAxis ? ` (${axisLabel(e.wildcardAxis)})` : '';
    return `${engagementKindLabel(e.kind)}${axis}${quantityCopy(e.kind, e.quantity)} · listing ${e.listingId} · ${atCopy(e.occurredUtc)}`;
}

/** The note that the list is cut: only the newest `cap` events in the window are shown. */
export function truncationCopy(cap: number, windowDays: number): string {
    return `Only the newest ${cap} events in the last ${windowDays} days are shown; older ones are cut off.`;
}
